import { useEffect, useState } from "react";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { useCheckoutForms } from "@shared/modules/checkout/hooks/useCheckoutForms";
import { useTranslation } from "@shared/utils/internationalization";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import { PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import InformationBanner from "@shared/components/ui-kit/data-display/InformationBanner/InformationBanner.web";
import PaymentTypeAccordion from "../../../../payment-type-accordion/PaymentTypeAccordion.web";
import { CardPaymentTypeManualCardEntry } from "../../../../payment-type-accordion/CardPaymentType/CardPaymentType.web";

export function PaymentSectionWaitlistBaskets({
  publicBasket,
  checkoutForms,
  onValid,
  onInvalid,
}: {
  publicBasket: ReturnType<typeof usePublicBasket>;
  checkoutForms: ReturnType<typeof useCheckoutForms>;
  onValid: () => void;
  onInvalid: () => void;
}) {
  const ccVerificationRequired =
    publicBasket.waitlist?.cc_verification_required && !publicBasket.isFree;

  if (!ccVerificationRequired) {
    return (
      <PaymentSectionWaitlistBaskets.NoCCVerificationRequired
        onValid={onValid}
      />
    );
  }

  return (
    <PaymentSectionWaitlistBaskets.CCVerificationRequired
      publicBasket={publicBasket}
      checkoutForms={checkoutForms}
      isBasketUpdating={publicBasket.isBasketUpdating}
      onValid={onValid}
      onInvalid={onInvalid}
    />
  );
}

PaymentSectionWaitlistBaskets.NoCCVerificationRequired =
  function PaymentSectionWaitlistBasketsNoCCVerificationRequired({
    onValid,
  }: {
    onValid: () => void;
  }) {
    const { t } = useTranslation("checkout");

    useEffect(() => {
      // No payment required, no billing address required. Instantly valid
      onValid();
    }, []);

    return (
      <Box marginTop="md" marginBottom="md">
        <Text fontSize="md" color="font.dark">
          {t("Payment information is not required to join the waitlist.")}
        </Text>
      </Box>
    );
  };

PaymentSectionWaitlistBaskets.CCVerificationRequired =
  function PaymentSectionWaitlistBasketsCCVerificationRequired({
    publicBasket,
    checkoutForms,
    isBasketUpdating,
    onValid,
    onInvalid,
  }: {
    publicBasket: ReturnType<typeof usePublicBasket>;
    checkoutForms: ReturnType<typeof useCheckoutForms>;
    isBasketUpdating: boolean;
    onValid: () => void;
    onInvalid: () => void;
  }) {
    const { t } = useTranslation("checkout");

    const [paymentValidationState, setPaymentValidationState] = useState<
      Record<number, boolean>
    >({});

    const handlePaymentValidationChange = (
      paymentType: number,
      isValid: boolean
    ) => {
      setPaymentValidationState((prevState) => ({
        ...prevState,
        [paymentType]: isValid, // Store validation status for each payment type
      }));
    };

    /**
     * useEffect responsible for calling validation callback
     */
    useEffect(() => {
      const isPaymentTypeValid =
        paymentValidationState[PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id] ?? false;

      if (isPaymentTypeValid) {
        onValid();
      } else {
        onInvalid();
      }
    }, [paymentValidationState]);

    return (
      <Box display="flex" flexDirection="column" gap="md">
        <Box>
          <InformationBanner.Info data-testid="waitlist-payment-info-banner">
            <InformationBanner.Header>
              {t("Why do we need your credit card?")}
            </InformationBanner.Header>
            <InformationBanner.Content>
              <Text>
                {t(
                  "If tickets become available, we'll automatically charge your card and issue your tickets. You can cancel your request and leave the waitlist anytime."
                )}
              </Text>
              <Text
                fontWeight={700}
                textDecoration="underline"
                fontSize="sm"
                mt="sm"
              >
                {t(
                  "Your credit card will not be charged until tickets become available"
                )}
              </Text>
            </InformationBanner.Content>
          </InformationBanner.Info>
        </Box>

        <Box>
          <PaymentTypeAccordion value={["0"]}>
            <CardPaymentTypeManualCardEntry
              onSelect={() => {
                // noop - waitlist doesn't need to change payment type
              }}
              onValidationChange={(isValid) =>
                handlePaymentValidationChange(
                  PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
                  isValid
                )
              }
              isDisabled={isBasketUpdating}
              shouldSaveCard={publicBasket.saveUserPaymentMethod}
              onToggleSaveCard={(shouldSaveCard) => {
                publicBasket.updateSaveUserPaymentMethod(shouldSaveCard, {
                  shouldPersist: false,
                });
              }}
              manualCardEntryForm={checkoutForms.manualCardEntryForm}
              addressForm={checkoutForms.billingAddressForm}
              userHasSavedCards={false}
              supportsSavingCards={false}
            />
          </PaymentTypeAccordion>
        </Box>
      </Box>
    );
  };
